import { useState } from 'react'
import {
  Fingerprint, FileText, ScanFace, ShieldCheck, Smartphone, Loader2, PartyPopper, ArrowRight, ArrowLeft,
  Check, Code2, ChevronDown, ChevronUp, RotateCcw, FlaskConical,
} from 'lucide-react'
import { InteractionDiagram } from './InteractionDiagram'
import { SimulatedVerificationPanel } from './IdvBiometricsStage'
import type { ChecklistItem, IdvFlowConfig } from '../types'
import type { IdvDiagramConfig } from './IdvConfigPanel'

const CHECKLIST_ICONS: Record<string, typeof FileText> = {
  'doc-capture': FileText,
  'face-match': ScanFace,
  consent: ShieldCheck,
  'hosted-sdk': Smartphone,
}

const CHECKLIST_PLAIN_DESCRIPTIONS: Record<string, string> = {
  'doc-capture': 'We read the passport, licence or ID card and check it hasn’t been tampered with.',
  'face-match': 'A quick selfie confirms the person holding the phone is the person in the ID photo.',
  consent: 'The customer sees and agrees to what’s being collected before anything happens.',
  'hosted-sdk': 'A ready-made, secure screen handles the whole thing — nothing to build yourselves.',
}

const STEPS = ['Overview', 'What to include', 'Try it', 'Result'] as const
type Step = 0 | 1 | 2 | 3

interface IdvGuidedViewProps {
  activeFlow: IdvFlowConfig
  hostedUrl: string | null
  loading: boolean
  hasCreds: boolean
  entityId: string | null
  events: { ts: string; [k: string]: unknown }[]
  customerName: string | null
  onGenerateUrl: () => void
  simulating: boolean
  simulated: boolean
  onSimulate: () => void
  checklistItems: ChecklistItem[]
  checklistEnabled: Record<string, boolean>
  onToggleChecklistItem: (id: string) => void
  diagramDsl: string
  activeStepMatch?: RegExp | null
  flowId: string
  onActiveFlowChange: (flowId: string) => void
  onDiagramConfigChange: (config: IdvDiagramConfig) => void
}

function ProgressBar({ step }: { step: Step }) {
  return (
    <div className="flex items-center gap-2">
      {STEPS.map((label, i) => (
        <div key={label} className="flex items-center gap-2 flex-1">
          <div className="flex items-center gap-2 flex-1">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
              style={i <= step
                ? { background: '#6366f1', color: 'white' }
                : { background: '#e5e7eb', color: '#64748b' }
              }
            >
              {i < step ? <Check size={12} /> : i + 1}
            </div>
            <span className="text-[11px] font-medium hidden sm:inline" style={{ color: i <= step ? '#4f46e5' : '#475569' }}>{label}</span>
          </div>
          {i < STEPS.length - 1 && <div className="h-[2px] flex-1" style={{ background: i < step ? '#6366f1' : '#e5e7eb' }} />}
        </div>
      ))}
    </div>
  )
}

export function IdvGuidedView({
  activeFlow, hostedUrl, loading, hasCreds, entityId, events, customerName, onGenerateUrl,
  simulating, simulated, onSimulate,
  checklistItems, checklistEnabled, onToggleChecklistItem, diagramDsl, activeStepMatch,
}: IdvGuidedViewProps) {
  const [step, setStep] = useState<Step>(0)
  const [showTechnical, setShowTechnical] = useState(false)

  function next() { setStep((s) => (Math.min(3, s + 1) as Step)) }
  function back() { setStep((s) => (Math.max(0, s - 1) as Step)) }
  function restart() { setStep(0); setShowTechnical(false) }

  return (
    <div className="space-y-5">
      <ProgressBar step={step} />

      {step === 0 && (
        <div className="rounded-[18px] p-8 text-center" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,.12), rgba(167,139,250,.06))', border: '1px solid rgba(99,102,241,.25)' }}>
          <div className="w-16 h-16 rounded-[18px] flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(99,102,241,.18)' }}>
            <Fingerprint size={30} className="text-[#4f46e5]" />
          </div>
          <h2 className="text-[22px] font-bold text-[#1e293b]">Identity Verification</h2>
          <p className="text-[14px] text-[#4f46e5] mt-2 max-w-md mx-auto leading-relaxed">
            Before {customerName ?? 'a customer'} can do anything sensitive, we confirm they&apos;re really who they say they are —
            a photo of an ID document plus a quick selfie, done in under a minute from any phone or laptop.
          </p>
          <button
            onClick={next}
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-[10px] text-[14px] font-semibold text-white transition-all"
            style={{ background: '#6366f1', boxShadow: '0 4px 16px rgba(99,102,241,.5)' }}
          >
            Get started <ArrowRight size={15} />
          </button>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <div>
            <h3 className="text-[16px] font-semibold text-[#1e293b]">What should be included?</h3>
            <p className="text-[12px] text-[#475569] mt-1">Tap anything that isn&apos;t needed for this customer to leave it out — everything else runs by default.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {checklistItems.map((item) => {
              const Icon = CHECKLIST_ICONS[item.id] ?? FileText
              const active = checklistEnabled[item.id] ?? true
              return (
                <button
                  key={item.id}
                  onClick={() => onToggleChecklistItem(item.id)}
                  className="text-left rounded-[14px] p-4 transition-all"
                  style={active
                    ? { background: '#f8fafc', border: '1px solid rgba(99,102,241,.35)' }
                    : { background: '#f8fafc', border: '1px solid #e5e7eb', opacity: 0.5 }
                  }
                >
                  <Icon size={20} className={active ? 'text-[#4f46e5]' : 'text-[#475569]'} />
                  <p className="text-[13px] font-semibold text-[#1e293b] mt-2">{item.label}</p>
                  <p className="text-[12px] text-[#475569] mt-1 leading-snug">{CHECKLIST_PLAIN_DESCRIPTIONS[item.id] ?? ''}</p>
                </button>
              )
            })}
          </div>
          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button onClick={next} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-[9px] text-[13px] font-semibold text-white" style={{ background: '#6366f1' }}>
              Next <ArrowRight size={13} />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div>
            <h3 className="text-[16px] font-semibold text-[#1e293b]">Try it yourself</h3>
            <p className="text-[12px] text-[#475569] mt-1">This is a real, live verification session — you&apos;ll need a camera to finish it.</p>
          </div>
          <div className="rounded-[18px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
            <div className="p-5 flex items-center justify-between flex-wrap gap-3">
              <p className="text-[13px] text-[#475569]">Flow: <span className="text-[#1e293b] font-semibold">{activeFlow.name}</span></p>
              <div className="flex items-center gap-2">
                <button
                  onClick={onSimulate}
                  disabled={loading || simulating}
                  className="px-4 py-2.5 rounded-[10px] text-[13px] font-semibold transition-all disabled:opacity-40 flex items-center gap-1.5"
                  style={{ background: 'rgba(167,139,250,.12)', border: '1px solid rgba(167,139,250,.35)', color: '#7c3aed' }}
                >
                  {simulating
                    ? <span className="flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Simulating…</span>
                    : <><FlaskConical size={14} /> Simulate</>}
                </button>
                <button
                  onClick={onGenerateUrl}
                  disabled={loading || simulating || !hasCreds}
                  className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                  style={{ background: '#6366f1', boxShadow: '0 4px 16px rgba(99,102,241,.5)' }}
                >
                  {loading
                    ? <span className="flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading…</span>
                    : 'Start verification'}
                </button>
              </div>
            </div>
            {!hasCreds && <p className="text-[12px] text-amber-700 text-center pb-3">No account for this customer yet? Use &quot;Simulate&quot; instead, or configure credentials above.</p>}
            {hostedUrl ? (
              <iframe
                src={hostedUrl}
                allow="camera;fullscreen;accelerometer;gyroscope;magnetometer;geolocation;microphone;"
                className="w-full"
                style={{ minHeight: 520, border: 0, borderTop: '1px solid #e5e7eb' }}
                title="FrankieOne Hosted Verification"
              />
            ) : (simulating || simulated) ? (
              <div style={{ borderTop: '1px solid #e5e7eb' }}>
                <SimulatedVerificationPanel eventsCount={events.length} />
              </div>
            ) : null}
          </div>
          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button onClick={next} disabled={!entityId} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-[9px] text-[13px] font-semibold text-white disabled:opacity-40" style={{ background: '#6366f1' }}>
              See result <ArrowRight size={13} />
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div className="rounded-[18px] p-6 text-center" style={{ background: 'rgba(52,211,153,.06)', border: '1px solid rgba(52,211,153,.25)' }}>
            <PartyPopper size={28} className="text-emerald-700 mx-auto mb-3" />
            <h3 className="text-[17px] font-semibold text-[#1e293b]">{simulated ? 'Verification simulated' : 'Verification started'}</h3>
            <p className="text-[13px] text-[#475569] mt-1">
              {simulated
                ? `A simulated session walked through ${events.length} step${events.length !== 1 ? 's' : ''} — no real FrankieOne call was made.`
                : events.length > 0
                  ? `${customerName ?? 'The customer'} completed ${events.length} step${events.length !== 1 ? 's' : ''} in the hosted session.`
                  : `Once ${customerName ?? 'the customer'} finishes the hosted session, results land here automatically.`}
            </p>
          </div>

          <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
            <button
              onClick={() => setShowTechnical((s) => !s)}
              className="w-full flex items-center justify-between px-4 py-3 transition-colors hover:bg-[#ffffff]"
              style={{ background: '#f8fafc' }}
            >
              <span className="flex items-center gap-2 text-[12px] font-semibold text-[#475569]">
                <Code2 size={13} /> See how this works technically
              </span>
              {showTechnical ? <ChevronUp size={13} className="text-[#64748b]" /> : <ChevronDown size={13} className="text-[#64748b]" />}
            </button>
            {showTechnical && (
              <div className="p-4" style={{ background: '#ffffff' }}>
                <InteractionDiagram stageId="idv" dsl={diagramDsl} activeStepMatch={activeStepMatch} />
              </div>
            )}
          </div>

          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button onClick={restart} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <RotateCcw size={13} /> Start over
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
